import {
  QuestionnaireParams,
  QuestionnaireType,
  SubmitParams,
} from "@/components/questionnaire/questionnaire.types.ts";

import {
  useEmployeeSurveyFormBySurveyId,
  useEmployeeSurveySaveDraft,
  useEmployeeSurveySubmitResults,
} from "@/queries/employees/use-employee-survey.query.ts";
import {
  useEmployeeScoreCardFormByFeedback,
  useEmployeeScoreCardResultsForFeedback,
  useScoreCardFeedbackSaveDraft,
  useScoreCardFeedbackSubmitResults,
} from "@/queries/recruit/use-scorecards.query.ts";
import { useSurveyParticipantResults } from "@/queries/surveys/use-surveys.query.ts";

import { ApiFormView } from "@/types/forms/form-view.types.ts";
import { ApiFormType } from "@/types/forms/form.types.ts";
import { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import {
  useEmployeePerformanceReviewForm,
  useEmployeePerformanceReviewSaveDraft,
  useEmployeePerformanceReviewSubmitResults,
} from "@/queries/employees/use-employee-performance-review.query.ts";
import { usePerformanceReviewReviewerResults } from "@/queries/performance-reviews/use-performance-reviews.query.ts";
import { ApiPassException } from "@/types/api.types.ts";

export const questionnaireTypes: Record<ApiFormType, QuestionnaireType> = {
  survey: {
    type: ApiFormType.survey,
    hooks: {
      useGetQuestionnaireForm: (params: QuestionnaireParams): UseQueryResult<ApiFormView> => {
        if (params.employeeId == undefined) throw new ApiPassException("Employee id is required");
        return useEmployeeSurveyFormBySurveyId({
          employeeId: params.employeeId,
          surveyId: params.questionnaireId,
          enabled: params.enabled,
        });
      },
      useSaveDraft: (): UseMutationResult<void, Error, SubmitParams, unknown> => {
        return useEmployeeSurveySaveDraft();
      },
      useSubmitQuestionnaire: (): UseMutationResult<void, Error, SubmitParams, unknown> => {
        return useEmployeeSurveySubmitResults();
      },
      useGetQuestionnaireResults: (params: QuestionnaireParams): UseQueryResult<ApiFormView> => {
        if (params.employeeId == undefined) throw new ApiPassException("Employee id is required");
        return useSurveyParticipantResults(params.questionnaireId, params.employeeId);
      },
    },
  },
  scoreCard: {
    type: ApiFormType.scoreCard,
    hooks: {
      useGetQuestionnaireForm: (params: QuestionnaireParams): UseQueryResult<ApiFormView> => {
        if (params.employeeId == undefined) throw new ApiPassException("Employee id is required");
        return useEmployeeScoreCardFormByFeedback({
          employeeId: params.employeeId,
          feedbackId: params.questionnaireId,
          enabled: params.enabled,
        });
      },
      useSaveDraft: (): UseMutationResult<void, Error, SubmitParams, unknown> => {
        return useScoreCardFeedbackSaveDraft();
      },
      useSubmitQuestionnaire: (): UseMutationResult<void, Error, SubmitParams, unknown> => {
        return useScoreCardFeedbackSubmitResults();
      },
      useGetQuestionnaireResults: (params: QuestionnaireParams): UseQueryResult<ApiFormView> => {
        if (params.employeeId == undefined) throw new ApiPassException("Employee id is required");
        return useEmployeeScoreCardResultsForFeedback({
          feedbackId: params.questionnaireId,
          employeeId: params.employeeId,
          enabled: true,
        });
      },
    },
  },
  performanceReview: {
    type: ApiFormType.performanceReview,
    hooks: {
      useGetQuestionnaireForm: (params: QuestionnaireParams): UseQueryResult<ApiFormView> => {
        if (params.employeeId == undefined) throw new ApiPassException("Employee id is required");
        return useEmployeePerformanceReviewForm({
          employeeId: params.employeeId,
          reviewerId: params.questionnaireId,
          enabled: params.enabled,
        });
      },
      useSaveDraft: (): UseMutationResult<void, Error, SubmitParams, unknown> => {
        return useEmployeePerformanceReviewSaveDraft();
      },
      useSubmitQuestionnaire: (): UseMutationResult<void, Error, SubmitParams, unknown> => {
        return useEmployeePerformanceReviewSubmitResults();
      },
      useGetQuestionnaireResults: (params: QuestionnaireParams): UseQueryResult<ApiFormView> => {
        if (params.respondentUuid == undefined)
          throw new ApiPassException("Respondent uuid is required");

        return usePerformanceReviewReviewerResults({
          reviewerTypeId: params.questionnaireId,
          respondentUuid: params.respondentUuid,
          enabled: params.enabled,
        });
      },
    },
  },
};
